import React, { useState, useEffect } from 'react';
import { Cpu, Check, AlertTriangle } from 'lucide-react';
import { apiGet, apiPost } from '../hooks/useApi';

const PROVIDERS = [
  { id: 'openai', name: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'], keyPlaceholder: 'sk-...' },
  { id: 'anthropic', name: 'Anthropic (Claude)', models: ['claude-sonnet-4-20250514', 'claude-3-5-sonnet-20241022', 'claude-3-haiku-20240307'], keyPlaceholder: 'sk-ant-...' },
  { id: 'deepseek', name: 'DeepSeek (Free Tier Available)', models: ['deepseek-chat', 'deepseek-reasoner'], keyPlaceholder: 'sk-...' },
  { id: 'gemini', name: 'Google Gemini', models: ['gemini-1.5-flash', 'gemini-1.5-pro', 'gemini-2.0-flash-exp'], keyPlaceholder: 'AIza...' },
  { id: 'openrouter', name: 'OpenRouter', models: ['deepseek/deepseek-chat-v3-0324:free', 'deepseek/deepseek-r1-0528:free', 'tngtech/deepseek-r1t2-chimera:free', 'openrouter/free'], keyPlaceholder: 'sk-or-...' },
  { id: 'custom', name: 'Custom (Any OpenAI-Compatible URL)', models: [], keyPlaceholder: 'your-api-key', needsUrl: true },
];

export default function AISettings() {
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasKey, setHasKey] = useState(false);

  useEffect(() => {
    apiGet('/ai/settings').then(data => {
      if (data) {
        setProvider(data.provider || '');
        setModel(data.model || '');
        setCustomUrl(data.custom_url || '');
        setHasKey(data.api_key_set || false);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const selectedProvider = PROVIDERS.find(p => p.id === provider);

  const handleSave = async () => {
    try {
      await apiPost('/ai/settings', { provider, model, api_key: apiKey || undefined, custom_url: customUrl || undefined });
      setSaved(true);
      setHasKey(true);
      setApiKey('');
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {}
  };

  if (loading) return <div className="card animate-pulse h-40" />;

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-accent-purple/10 border border-accent-purple/20 flex items-center justify-center">
          <Cpu size={18} className="text-accent-purple" />
        </div>
        <div>
          <h3 className="text-sm font-semibold font-display text-gray-100">AI EA Analyzer</h3>
          <p className="text-[11px] text-gray-500">Configure AI to analyze your EA performance</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Provider Select */}
        <div>
          <label className="text-xs text-gray-400 mb-1.5 block">AI Provider</label>
          <select
            value={provider}
            onChange={e => { setProvider(e.target.value); setModel(''); }}
            className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2.5 text-sm text-gray-200"
          >
            <option value="">Select provider...</option>
            {PROVIDERS.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Model Select */}
        {selectedProvider && selectedProvider.models.length > 0 && (
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Model</label>
            <select
              value={model}
              onChange={e => setModel(e.target.value)}
              className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2.5 text-sm text-gray-200"
            >
              <option value="">Default ({selectedProvider.models[0]})</option>
              {selectedProvider.models.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        )}

        {/* Custom URL (for custom/opencode providers) */}
        {selectedProvider?.needsUrl && (
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">API Endpoint URL</label>
            <input
              type="text"
              value={customUrl}
              onChange={e => setCustomUrl(e.target.value)}
              placeholder="https://opencode.ai/api/v1/chat/completions"
              className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600"
            />
            <p className="text-[10px] text-gray-600 mt-1">Must be OpenAI-compatible (/v1/chat/completions)</p>
          </div>
        )}

        {/* Free-text model name for custom provider */}
        {selectedProvider?.needsUrl && (
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Model Name</label>
            <input
              type="text"
              value={model}
              onChange={e => setModel(e.target.value)}
              placeholder="deepseek-v4-flash-free"
              className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600"
            />
          </div>
        )}

        {/* API Key */}
        {selectedProvider && (
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">
              API Key {hasKey && <span className="text-accent-green ml-1">✓ saved</span>}
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={hasKey ? '••••••••••••• (already set)' : selectedProvider.keyPlaceholder}
              className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600"
            />
            <p className="text-[10px] text-gray-600 mt-1">Your key is stored encrypted. Never shared.</p>
          </div>
        )}

        {/* Save Button */}
        {selectedProvider && (
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-accent-green text-dark-bg font-semibold rounded-lg text-sm hover:opacity-90 transition"
          >
            {saved ? <><Check size={14} /> Saved!</> : 'Save AI Settings'}
          </button>
        )}

        {/* Info */}
        <div className="bg-dark-bg border border-dark-border rounded-lg p-3 mt-4">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-accent-orange mt-0.5 flex-shrink-0" />
            <div className="text-[11px] text-gray-500 space-y-1">
              <p>AI Analysis uses your personal API key — charges apply from your AI provider.</p>
              <p>Analysis works best with 30+ closed trades for accurate assessment.</p>
              <p className="text-gray-600">Free: Get DeepSeek API key at <code className="text-gray-400">platform.deepseek.com</code> (free tier included)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
